import { useRef, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useLocale } from '../i18n/LocaleContext'

export function AppModal({
  title,
  closeLabel,
  onClose,
  wide = false,
  extraClassName = '',
  feedback,
  backLabel,
  previousLabel,
  nextLabel,
  onPrevious,
  onNext,
  saveForm = false,
  onSave,
  saveDisabled = false,
  headerActions,
  onDelete,
  deleteLabel,
  deleteDisabled = false,
  children,
}: {
  title: string
  closeLabel: string
  onClose: () => void
  wide?: boolean
  extraClassName?: string
  feedback?: ReactNode
  backLabel?: string
  previousLabel?: string
  nextLabel?: string
  onPrevious?: (() => void) | null
  onNext?: (() => void) | null
  saveForm?: boolean
  onSave?: (() => void) | null
  saveDisabled?: boolean
  headerActions?: ReactNode
  onDelete?: (() => void) | null
  deleteLabel?: string
  deleteDisabled?: boolean
  children: ReactNode
}) {
  const surfaceRef = useRef<HTMLElement>(null)
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useLocale()
  const recordIds = (location.state as { recordIds?: string[] } | null)?.recordIds ?? []
  const currentSearch = new URLSearchParams(location.search)
  const currentId = currentSearch.get('edit') ?? currentSearch.get('id')
  const currentIndex = currentId ? recordIds.indexOf(currentId) : -1
  const goToSibling = (offset: number) => {
    const id = recordIds[currentIndex + offset]
    if (!id) return
    const next = new URLSearchParams(location.search); next.set(currentSearch.has('id') ? 'id' : 'edit', id); next.delete('new')
    navigate({ pathname: location.pathname, search: `?${next}` }, { state: { recordIds } })
  }
  const previous = onPrevious ?? (currentIndex > 0 ? () => goToSibling(-1) : null)
  const next = onNext ?? (currentIndex >= 0 && currentIndex < recordIds.length - 1 ? () => goToSibling(1) : null)
  const showSiblingNavigation = (currentIndex >= 0 && recordIds.length > 1) || Boolean(onPrevious || onNext)
  const save = onSave ?? (saveForm ? () => surfaceRef.current?.querySelector<HTMLFormElement>('.administration-modal-body form')?.requestSubmit() : null)
  return <div className="record-editor-page">
    <section
      ref={surfaceRef}
      className={`administration-modal record-editor-surface ${wide ? 'wide' : ''} ${save ? 'has-header-save' : ''} ${extraClassName}`.trim()}
      aria-labelledby="record-editor-title"
    >
      <header>
        <div className="record-editor-heading">
          <button className="secondary-button record-back-button" type="button"
            aria-label={backLabel ?? closeLabel} onClick={onClose}>
            ← {backLabel ?? t('recordNavigation.backToList')}
          </button>
          <h2 id="record-editor-title">{title}</h2>
        </div>
        {(save || onDelete || headerActions || showSiblingNavigation) && <div className="record-editor-header-actions">
          {showSiblingNavigation && <nav className="record-sibling-navigation" aria-label={title}>
            <button className="secondary-button" type="button" disabled={!previous}
              aria-label={previousLabel ?? t('recordNavigation.previous')} onClick={() => previous?.()}><span className="responsive-action-icon" aria-hidden="true">←</span><span className="responsive-action-label">{previousLabel ?? t('recordNavigation.previous')}</span></button>
            <button className="secondary-button" type="button" disabled={!next}
              aria-label={nextLabel ?? t('recordNavigation.next')} onClick={() => next?.()}><span className="responsive-action-label">{nextLabel ?? t('recordNavigation.next')}</span><span className="responsive-action-icon" aria-hidden="true">→</span></button>
          </nav>}
          {headerActions && <div className="record-editor-extra-actions">{headerActions}</div>}
          {onDelete && <button className="danger-button" type="button" disabled={deleteDisabled}
            aria-label={deleteLabel ?? t('deletion.delete')} onClick={() => onDelete()}><svg className="responsive-action-icon delete-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3m3 0-1 14H7L6 7m4 4v6m4-6v6" /></svg><span className="responsive-action-label">{deleteLabel ?? t('deletion.delete')}</span></button>}
          {save && <button className="primary-button record-save-button" type="button" disabled={saveDisabled}
            aria-label={t('administration.save')} onClick={() => save()}><svg className="responsive-action-icon save-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 3h12l2 2v16H5zM8 3v6h8V3M8 21v-7h8v7" /></svg><span className="responsive-action-label">{t('administration.save')}</span></button>}
        </div>}
      </header>
      {feedback && <div className="modal-feedback" role="alert" aria-live="assertive">
        {feedback}
      </div>}
      <div className="administration-modal-body">{children}</div>
    </section>
  </div>
}
